import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { PortalPageSection } from 'components/PortalStructure';
import { IBookAppointmentObject } from 'models/bookappointment';
import { IUserPreferences } from 'models/user/userpreferences';
import { Button, ErrorSummary } from 'components';
import { Field, reduxForm, getFormSyncErrors } from 'redux-form';
import { PortalCheckboxField, BookingInputField, BookingSelectMenu } from 'components/FormControls';
import { maxLength, phoneNumber as phoneNumberValidation, required } from 'helpers/validationattributes';
import { IStore } from 'redux/IStore';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { RouteComponentProps } from 'react-router';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { getUserShippingAddresses } from 'redux/modules/user/shipping/usershippingaddresses';
import { createDomainValueOptions, detectIncompatibleBrowsers, fullPhoneNumber } from 'helpers/common';
import { domainValuesMap } from 'redux/modules/domainvalues';
import renderIcon from 'helpers/renderIcon';
import { getUserProfile, putUserProfile } from 'redux/modules/user/userprofile';
import { IUserProfile } from 'models/user/userprofile';

type ReduxInjectedProps = Omit<
	Partial<ReturnType<typeof stateToProps> & ReturnType<typeof dispatchToProps>>,
	'initialValues'
>;

interface IProps
	extends ReduxInjectedProps,
		RouteComponentProps<{}, {}>,
		ITranslateProps,
		InjectedFormProps<IBookAppointmentObject> {
	route: any;
	userPreferences?: IUserPreferences;
	servicesParentIds: string[];
	mobileShopOrderDetailsElement: any;

	onSubmit?: (form: IBookAppointmentObject) => void;
	setStaffForService: (servicesParentId: string, resourceId: string | null) => boolean;
}

interface IState {
	selectedTab: string;
	isSomeoneElseTabActive: boolean;
	/**
	 * is customer editing user phone number
	 */
	editMode: boolean;
	changePhoneUserPreferences: boolean;
	showBookingDetails: boolean;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class BookAppointmentStepCustomersForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.state = {
			selectedTab: 'myselfonly',
			isSomeoneElseTabActive: false,
			editMode: false,
			changePhoneUserPreferences: false,
			showBookingDetails: false,
		};
	}

	public componentDidMount() {
		if (detectIncompatibleBrowsers()) {
			document.body.scrollTo({ top: 0, left: 0 });
		} else {
			window.scrollTo({ top: 0, left: 0 });
		}

		this.props.getUserProfile();

		if (!this.props.userShippingAddresses?.loaded && !this.props.userShippingAddresses?.isFetching) {
			this.props.getUserShippingAddresses();
		}

		if (!this.props.countryCallingCodes?.length) {
			this.props.getCountryCallingCodeDomainValues();
		}
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'otherCallingCodeKey':
			case 'callingCodeKey':
				return t('BSPortal_Common_Phone_Prefix');
			case 'otherphoneNumber':
			case 'phoneNumber':
				return t('PhoneNumber_Label');
			case 'name':
				return t('BookAppointment_Customer_Label_Name');
			case 'surname':
				return t('BookAppointment_Customer_Label_Surname');
			default:
				return key;
		}
	}

	public onSubmit(form: IBookAppointmentObject) {
		if (this.state.selectedTab === 'myselfonly') {
			this.props.change('activeUser', true);
		} else {
			this.props.change('activeUser', false);
		}
		this.props.onSubmit(JSON.parse(JSON.stringify(form)));
	}

	public render() {
		const { t, formErrors, submitFailed, userPreferences, handleSubmit, location, countryCallingCodes } = this.props;

		const { selectedTab } = this.state;

		const callingCodes = createDomainValueOptions(t, 'DV_CountryCallingCodes_', countryCallingCodes);

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		return (
			<form onSubmit={handleSubmit(this.onSubmit)} className="bookingAppointment__customerStep">
				<h1>{t('BookAppointment_Customer_Page_Title')}</h1>
				<PortalPageSection
					className={`bookingAppointment__customerStep__myselfOnly ${selectedTab === 'myselfonly' ? 'active' : ''}`}
					onClick={() => this.setState({ selectedTab: 'myselfonly' })}
				>
					<div className="bookingAppointment__customerStep__myselfOnly__upperBar">
						<span>{t('BookAppointment_Customer_Page_MyselfOnly')}</span>
						<div className="bookingAppointment__customerStep__myselfOnly__upperBar__editButtonsHolderDesktop">
							{!this.state.editMode &&
								renderIcon('editPencil', 20, 20, 'customerEdit', () => {
									this.setState((prev) => ({ editMode: !prev.editMode }));
								})}
							{this.state.editMode && (
								<>
									{renderIcon('check', 20, 20, 'customerCheck', () => {
										if (
											this.state.changePhoneUserPreferences &&
											this.props.currentFormValues.callingCodeKey &&
											this.props.currentFormValues.phoneNumber
										) {
											if (
												this.props.userprofile.user.phoneNumber !== this.props.currentFormValues.phoneNumber ||
												this.props.userprofile.user.callingCodeKey !== this.props.currentFormValues.callingCodeKey
											) {
												const user = {
													...this.props.userprofile.user,
													phoneNumber: this.props.currentFormValues.phoneNumber,
													callingCodeKey: this.props.currentFormValues.callingCodeKey,
												};
												this.props.changeUserProfile(user);
											}
										}

										this.setState((prev) => ({ editMode: !prev.editMode }));
									})}
									{renderIcon('cancel', 20, 20, 'customerCancel', () => {
										// If the user canceled their edit set the current form values back to their userPreferences data
										this.props.change('callingCodeKey', this.props.userPreferences.callingCodeKey);
										this.props.change('phoneNumber', this.props.userPreferences.phoneNumber);
										this.setState((prev) => ({ editMode: !prev.editMode }));
									})}
								</>
							)}
						</div>
					</div>
					<p className="customer-name">{userPreferences.name}</p>
					{!this.state.editMode && (
						<p className="customer-phone">
							{!this.props.currentFormValues
								? fullPhoneNumber(t, this.props.initialValues?.callingCodeKey, this.props.initialValues?.phoneNumber)
								: fullPhoneNumber(
										t,
										this.props.currentFormValues?.callingCodeKey,
										this.props.currentFormValues?.phoneNumber,
								  )}
						</p>
					)}
					{this.state.editMode && (
						<>
							<div className="row phone-number-div">
								<div className="col-5 col-sm-3">
									<Field
										name="callingCodeKey"
										component={BookingSelectMenu}
										items={callingCodes}
										isRequired={true}
										label={t('BSPortal_Common_Phone_Prefix')}
										validate={[required]}
										information={t('BSPortal_Common_Phone_Prefix')}
										placeholder={'+386'}
									/>
								</div>
								<div className="col-7 col-sm-6">
									<Field
										name="phoneNumber"
										component={BookingInputField}
										type="text"
										validate={[maxLength(50), required, phoneNumberValidation]}
										label={t('PhoneNumber_Label')}
										normalize={(val) => (val || '').replace(/[^\d]/g, '')}
										inputmode="numeric"
										pattern="[0-9]*"
										information={t('PhoneNumber_Label')}
										placeholder={'41 984 235'}
									/>
								</div>
							</div>
							<label className="apply-changes-notice">
								<PortalCheckboxField
									input={{
										checked: this.state.changePhoneUserPreferences,
										onChange: () =>
											this.setState((prev) => ({ changePhoneUserPreferences: !prev.changePhoneUserPreferences })),
									}}
									type="checkbox"
								/>
								{t('BookAppointment_Customer_Label_PhoneNumberNotice')}
							</label>
							<div className="bookingAppointment__customerStep__myselfOnly__editButtonsHolderMobile">
								{!this.state.editMode &&
									renderIcon('editPencil', 20, 20, 'customerEdit', () => {
										this.setState((prev) => ({ editMode: !prev.editMode }));
									})}
								{this.state.editMode && (
									<>
										<div
											className="customerCheckMobileHolder"
											onClick={() => {
												if (
													this.state.changePhoneUserPreferences &&
													this.props.currentFormValues.callingCodeKey &&
													this.props.currentFormValues.phoneNumber
												) {
													if (
														this.props.userprofile.user.phoneNumber !== this.props.currentFormValues.phoneNumber ||
														this.props.userprofile.user.callingCodeKey !== this.props.currentFormValues.callingCodeKey
													) {
														const user = {
															...this.props.userprofile.user,
															phoneNumber: this.props.currentFormValues.phoneNumber,
															callingCodeKey: this.props.currentFormValues.callingCodeKey,
														};
														this.props.changeUserProfile(user);
													}
												}

												this.setState((prev) => ({ editMode: !prev.editMode }));
											}}
										>
											{renderIcon('check', 20, 20, 'customerCheck')}
											<span>{t('BookAppointment_Customer_SaveChanges')}</span>
										</div>
										<div
											className="customerCancelMobileHolder"
											onClick={() => {
												// If the user canceled their edit set the current form values back to their userPreferences data
												this.props.change('callingCodeKey', this.props.userPreferences.callingCodeKey);
												this.props.change('phoneNumber', this.props.userPreferences.phoneNumber);
												this.setState((prev) => ({ editMode: !prev.editMode }));
											}}
										>
											{renderIcon('cancel', 20, 20, 'customerCancel')}
											<span>{t('BSPortal_Common_Cancel')}</span>
										</div>
									</>
								)}
							</div>
						</>
					)}
				</PortalPageSection>
				<div className="profileError">
					<ErrorSummary {...errorSummaryProps} />
				</div>
				<PortalPageSection
					className={`bookingAppointment__customerStep__someoneElse ${selectedTab === 'someoneelse' ? 'active' : ''}`}
					onClick={() => this.setState({ selectedTab: 'someoneelse', editMode: false })}
				>
					<div className="row gx-10px">
						<div className="col-md-12">
							<div className="bookingAppointment__customerStep__someoneElse__upperBar">
								{t('BookAppointment_Customer_Page_SomeoneElse')}
							</div>
						</div>
						{this.state.selectedTab === 'someoneelse' && (
							<>
								<div className="col-md-12">
									<p className="contactInformation">{t('BookAppointment_Customer_Provide_Information')}</p>
								</div>
								<div className="col-sm-6">
									<Field
										name="name"
										component={BookingInputField}
										type="text"
										validate={[required]}
										label={t('BookAppointment_Customer_Label_Name')}
										information={t('BookAppointment_Customer_Label_Name')}
										placeholder={t('BookAppointment_Customer_Label_PersonName')}
									/>
								</div>
								<div className="col-sm-6">
									<Field
										name="surname"
										component={BookingInputField}
										type="text"
										validate={[required]}
										label={t('BookAppointment_Customer_Label_Surname')}
										information={t('BookAppointment_Customer_Label_Surname')}
										placeholder={t('BookAppointment_Customer_Label_PersonSurname')}
									/>
								</div>
								<div className="col-5 col-sm-4">
									<Field
										name="otherCallingCodeKey"
										component={BookingSelectMenu}
										items={callingCodes}
										isRequired={true}
										label={t('BSPortal_Common_Phone_Prefix')}
										validate={[required]}
										information={t('BSPortal_Common_Phone_Prefix')}
										placeholder={'+386'}
									/>
								</div>
								<div className="col-7 col-sm-8">
									<Field
										name="otherPhoneNumber"
										component={BookingInputField}
										type="text"
										validate={[maxLength(50), required, phoneNumberValidation]}
										label={t('PhoneNumber_Label')}
										normalize={(val) => (val || '').replace(/[^\d]/g, '')}
										inputmode="numeric"
										pattern="[0-9]*"
										information={t('PhoneNumber_Label')}
										placeholder={'41 984 235'}
									/>
								</div>
							</>
						)}
					</div>
				</PortalPageSection>
				{this.state.showBookingDetails && <div className="bookingAppointment__shadow"></div>}
				<div className="bookingAppointment__bottomContent">
					<div className="container">
						<div className="row">
							<div className="col-lg-9 col-xl-6 offset-lg-3">
								<div className="bookingAppointment__bottomContent__bookingDetails">
									<div
										className="detailsSwitch"
										onClick={() => this.setState({ showBookingDetails: !this.state.showBookingDetails })}
									>
										<span>{t('BookAppointment_Booking_Details_Title')}</span>
										{this.state.showBookingDetails ? renderIcon('arrow_right') : renderIcon('arrow_left')}
									</div>
									<div className="detailsContent">
										{this.state.showBookingDetails && this.props.mobileShopOrderDetailsElement()}
									</div>
								</div>
								<div className="bookingAppointment__bottomContent__single">
									{/* Changing to "Someone else" tab fills props.formErrors, optimally it should be cleared when switching back to "Myself only" */}
									<Button
										type="submit"
										disabled={
											(Object.keys(formErrors || {}).length !== 0 && this.state.selectedTab !== 'myselfonly') ||
											this.state.editMode
										}
									>
										{t('BSPortal_Common_Continue')}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		);
	}
}

let BookAppointmentStepCustomers = reduxForm({
	form: c.FORM_NAME_BOOKAPPOINTMENT,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(BookAppointmentStepCustomersForm);

const stateToProps = (state: IStore, ownProps) => {
	let currentFormValues: IBookAppointmentObject = null;
	if (state.form[c.FORM_NAME_BOOKAPPOINTMENT] && state.form[c.FORM_NAME_BOOKAPPOINTMENT].values) {
		currentFormValues = state.form[c.FORM_NAME_BOOKAPPOINTMENT].values;
	}
	const initialValues: Partial<IBookAppointmentObject> = {
		callingCodeKey: ownProps.userPreferences.callingCodeKey,
		phoneNumber: ownProps.userPreferences.phoneNumber,
	};
	return {
		formErrors: getFormSyncErrors(c.FORM_NAME_BOOKAPPOINTMENT)(state),
		userShippingAddresses: state.userShippingAddresses,
		userprofile: state.userprofile,
		countryCallingCodes: state.domainValues.countryCallingCodes,
		currentFormValues,
		initialValues,
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	getUserShippingAddresses: () => dispatch(getUserShippingAddresses()),
	getUserProfile: () => dispatch(getUserProfile()),
	changeUserProfile: (user: IUserProfile) => dispatch(putUserProfile(user)),
	getCountryCallingCodeDomainValues: () => dispatch(domainValuesMap.countryCallingCodes()),
});

BookAppointmentStepCustomers = connect(stateToProps, dispatchToProps)(BookAppointmentStepCustomers);

export { BookAppointmentStepCustomers };
